import React, { useEffect, useState } from 'react';

/**
 * The shape of the two datasets passed to all components:
 * questionDataset: {
 *   ACCNUM: {
 *     id,
 *     accNum,
 *     interactionType,
 *     questionType,
 *     tags: [{
 *       header | title,
 *       hierarchy,
 *       tags: [{...}],
 *     }],
 *     title,
 *     totalPoints,
 *   }
 * }
 * studentDataset: {
 *   studentId: {
 *     items: [{
 *       assessment_group_order,
 *       attempted,
 *       correct,
 *       item_number,
 *       max_score,
 *       max_score_of_attempted,
 *       max_score_of_unmarked,
 *       not_scored,
 *       score,
 *       session_id,
 *       time_spent,
 *       unmarked,
 *     }],
 *     metadata: {
 *       id,
 *       name,
 *       first_name,
 *       last_name,
 *       time_completed,
 *     }
 *   }
 * }
 */

const withSyncedDatasets = (Component) => (props) => {
  const [newProps, setNewProps] = useState();

  const { lastScoreByItem } = props;

  useEffect(() => {
    let {
      studentDataset,
      questionDataset,
      reportLoaded,
      questionsLoaded,
      reportsData,
      classAveragePoints,
      totalAssessmentPoints,
      reviewUntilDate,
      isReviewDisabled,
      questionTagsById,
      relatedAssignments,
    } = props;
    // this parse+stringify looks weird, but it's a cheap way to deep copy objects and necessary since graphql does
    // mean things to make objects immutable
    let updatedQuestionDataset = JSON.parse(
      JSON.stringify(questionDataset || {}),
    );
    let updatedStudentDataset = JSON.parse(
      JSON.stringify(studentDataset || {}),
    );
    let questionsDidLoad = questionsLoaded;
    let reportDidLoad = reportLoaded;

    if (questionTagsById) {
      updatedQuestionDataset = JSON.parse(JSON.stringify(questionTagsById));
      Object.keys(updatedQuestionDataset).forEach((accNum) => {
        // many legacy places expect `learnosityItemReference`, but the graphql response uses `accNum`.
        updatedQuestionDataset[accNum].learnosityItemReference = accNum;
        // PerformanceItem and some others expect `type` but the graphql response calls this `questionType`.
        updatedQuestionDataset[accNum].type =
          updatedQuestionDataset[accNum].questionType;
      });
      questionsDidLoad = true;
    }

    if (lastScoreByItem) {
      // generally only one should come in here
      const firstAssessment = lastScoreByItem[Object.keys(lastScoreByItem)[0]];
      const {
        users,
        count_items: itemCount,
        class_average_score: updatedClassAveragePoints,
        total_assessment_points: updatedTotalAssessmentPoints,
      } = firstAssessment;

      classAveragePoints = updatedClassAveragePoints;
      totalAssessmentPoints = updatedTotalAssessmentPoints;
      const userIds = Object.keys(users);

      userIds.forEach((key) => {
        // state change, so new objects
        const userData = Object.assign({}, users[key]);
        const userItemKeys = Object.keys(userData.items);
        // PREAP-2076: if the leanosity report doesn't have data for all of the questions in the assessment, we filter the student out.
        // this essentially means that anyone who didn't submit both parts of a two-part exam won't appear.
        if (userItemKeys.length < itemCount) {
          return;
        }
        updatedStudentDataset[key] = userData;
        // set the items from the results
        userItemKeys.forEach((itemId) => {
          if (updatedQuestionDataset[itemId]) {
            updatedQuestionDataset[itemId].totalPoints =
              userData.items[itemId].max_score;
            updatedQuestionDataset[itemId].assessmentGroupSortOrder =
              userData.items[itemId].assessment_group_order;
          }
        });
      });

      // if any of the assignments are marked with isReviewDisabled, then the whole thing gets disabled.
      isReviewDisabled = false;
      reviewUntilDate = null;

      if (relatedAssignments) {
        relatedAssignments.forEach((a) => {
          isReviewDisabled = a.isReviewDisabled || isReviewDisabled;
          if (a.reviewUntil) {
            reviewUntilDate = Math.max(
              Date.parse(a.reviewUntil),
              reviewUntilDate,
            );
          }
        });
      }
      reportDidLoad = true;
    }

    setNewProps({
      studentDataset: updatedStudentDataset,
      questionDataset: updatedQuestionDataset,
      reportsData: lastScoreByItem || reportsData,
      lastScoreByItem,
      classAveragePoints,
      totalAssessmentPoints,
      reportLoaded: reportDidLoad,
      questionsLoaded: questionsDidLoad,
      datasetsLoaded: reportDidLoad && questionsDidLoad,
      reviewUntilDate,
      isReviewDisabled,
    });
  }, [lastScoreByItem]);

  return <Component {...props} {...newProps} />;
};

export default withSyncedDatasets;
